import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";

import { useLenis } from "@/components/layout/SmoothScrollProvider";
import { Button } from "@/components/ui/button";

const DEFAULT_SECTION_SCROLL_OFFSET = -80;

const getSectionScrollOffset = (element) => {
  if (typeof window === "undefined" || !element) {
    return DEFAULT_SECTION_SCROLL_OFFSET;
  }

  const scrollMarginTop = window.getComputedStyle(element).scrollMarginTop;
  const parsedMarginTop = Number.parseFloat(scrollMarginTop);

  if (!Number.isFinite(parsedMarginTop) || parsedMarginTop <= 0) {
    return DEFAULT_SECTION_SCROLL_OFFSET;
  }

  return -parsedMarginTop;
};

const NavbarPublic = () => {
  const lenisRef = useLenis();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const mobileMenuCloseRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleScroll = () => {
      const scrolled = window.scrollY > 30;
      setIsScrolled(prev => prev === scrolled ? prev : scrolled);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!isMobileMenuOpen) return;

    const lenis = lenisRef?.current;
    lenis?.stop();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      lenis?.start();
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen, lenisRef]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previouslyFocused = document.activeElement;
    mobileMenuCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsMobileMenuOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = mobileMenuRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    
    if (!isHomePage) {
      window.location.href = `/#${id}`;
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      const sectionOffset = getSectionScrollOffset(element);
      if (lenisRef?.current) {
        lenisRef.current.scrollTo(element, { offset: sectionOffset });
      } else {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const scrollToTop = () => {
    setIsMobileMenuOpen(false);
    if (lenisRef?.current) {
      lenisRef.current.scrollTo(0, { immediate: false });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "glass-heavy shadow-xl shadow-black/20" 
            : "bg-transparent"
        }`}
      >
        <div className="lenvia-container">
          <div className="flex items-center justify-between h-[72px] md:h-[80px]">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center group" 
              data-testid="logo-link" 
              onClick={scrollToTop} 
              aria-label="Retour en haut de la page"
            >
              <img 
                src="/lenvia-logo.png" 
                alt="Lenvia by Proxiad" 
                className="h-9 md:h-10 w-auto transition-opacity group-hover:opacity-80" 
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10">
              <button 
                onClick={() => scrollToSection("expertises")} 
                className="nav-link" 
                data-testid="nav-expertises"
              >
                Expertises
              </button>
              <button 
                onClick={() => scrollToSection("qui-sommes-nous")} 
                className="nav-link" 
                data-testid="nav-pourquoi"
              >
                Qui sommes-nous ?
              </button>
              <button 
                onClick={() => scrollToSection("secteurs")} 
                className="nav-link" 
                data-testid="nav-references"
              >
                Secteurs
              </button>
              <button 
                onClick={() => scrollToSection("carrieres")} 
                className="nav-link" 
                data-testid="nav-carrieres"
              >
                Carrières
              </button>
              <Button 
                onClick={() => scrollToSection("contact")} 
                className="btn-primary text-sm px-7 py-3" 
                data-testid="header-cta"
              >
                Nous contacter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </nav>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2.5 text-white hover:bg-white/10 rounded-xl transition-colors" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? "Fermer le menu principal" : "Ouvrir le menu principal"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Portal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                ref={mobileMenuRef}
                id="mobile-navigation"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="lg:hidden fixed inset-0 w-full h-[100dvh] z-[9999] bg-[#070B1A]"
                role="dialog"
                aria-modal="true"
                aria-label="Menu principal"
              >
                <div className="lenvia-container h-full pt-6 pb-8 overflow-y-auto">
                  {/* Mobile Header */}
                  <div className="h-16 flex items-center justify-between">
                    <img 
                      src="/lenvia-logo.png" 
                      alt="Lenvia" 
                      className="h-9 w-auto" 
                    />
                    <button
                      ref={mobileMenuCloseRef}
                      className="p-2.5 text-white hover:bg-white/10 rounded-xl transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid="mobile-menu-close"
                      aria-label="Fermer le menu mobile"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  {/* Mobile Navigation */}
                  <nav className="pt-12 flex flex-col gap-3">
                    {[
                      { id: "expertises", label: "Expertises" },
                      { id: "qui-sommes-nous", label: "Qui sommes-nous ?" },
                      { id: "secteurs", label: "Secteurs" },
                      { id: "carrieres", label: "Carrières" }
                    ].map((item) => (
                      <button 
                        key={item.id}
                        onClick={() => scrollToSection(item.id)} 
                        className="text-left py-5 px-4 text-xl font-semibold text-slate-200 hover:text-white hover:bg-white/[0.05] rounded-xl transition-colors" 
                        data-testid={`mobile-nav-${item.id}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    <div className="pt-8 mt-6 border-t border-white/[0.08]">
                      <Button 
                        onClick={() => scrollToSection("contact")} 
                        className="btn-primary w-full justify-center py-5 text-base" 
                        data-testid="mobile-header-cta"
                      >
                        Nous contacter
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </nav>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
};

export default NavbarPublic;
